import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle, Mail, Phone, MapPin, Clock } from "lucide-react";
import { getAllServices } from "@/data/navigationData";
import { trackEvent, trackException } from "@/lib/firebase";

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const preSelectedService = searchParams.get("service") || "";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: preSelectedService,
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Update service if coming from a service page
  useEffect(() => {
    if (preSelectedService) {
      setFormData(prev => ({ ...prev, service: preSelectedService }));
    }
  }, [preSelectedService]);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://finease-crm.vercel.app';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    trackEvent("contact_submit_attempt", {
      service_name: formData.service || "",
      has_company: Boolean(formData.company),
      has_message: Boolean(formData.message),
    });

    try {
      const response = await fetch(`${API_BASE_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          service: formData.service,
          message: formData.message,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Submission failed');
      }

      setIsSubmitted(true);
      toast.success("Thank you! We'll be in touch within 24 hours.");
      trackEvent("contact_submitted", {
        service_name: formData.service || "",
      });
    } catch (error) {
      console.error('Lead submission error:', error);
      toast.error('Something went wrong. Please try again.');
      trackEvent("contact_submit_failed", {
        service_name: formData.service || "",
        reason: error?.message || "unknown",
      });
      trackException("contact_submit_request_failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get all services from navigation data grouped by category
  const allServices = getAllServices();
  const serviceCategories = [
    { label: "Registrations", services: allServices.filter(s => s.menuId === "registrations") },
    { label: "Incorporations", services: allServices.filter(s => s.menuId === "incorporations") },
    { label: "Compliance", services: allServices.filter(s => s.menuId === "compliance") },
    { label: "Tax & GST", services: allServices.filter(s => s.menuId === "tax-gst") },
    { label: "Licenses", services: allServices.filter(s => s.menuId === "licenses") },
    { label: "Intellectual Property", services: allServices.filter(s => s.menuId === "intellectual-property") },
    { label: "Legal Documents", services: allServices.filter(s => s.menuId === "legal-documents") },
    { label: "Disputes", services: allServices.filter(s => s.menuId === "disputes") },
    { label: "CXO Services", services: allServices.filter(s => s.menuId === "cxo-services") },
  ];

  const contactInfo = [
    {
      icon: Mail,
      label: "Email",
      value: "info@fineaseconsulting.com",
      href: "mailto:info@fineaseconsulting.com",
    },
    {
      icon: Phone,
      label: "Phone",
      value: "+91 9899462024",
      href: "tel:+919899462024",
    },
    {
      icon: MapPin,
      label: "Office",
      value: "Keshav Puram Delhi",
      href: null,
    },
    {
      icon: Clock,
      label: "Hours",
      value: "Mon - Sat: 10am - 7pm IST",
      href: null,
    },
  ];

  return (
    <main data-testid="contact-page" className="pt-24 md:pt-32 bg-[#050505]">
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <span className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-4 block">
            Get in Touch
          </span>
          <h1
            className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-6"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Contact Us
          </h1>
          <p className="text-lg md:text-xl text-slate-400 leading-relaxed">
            Ready to transform your financial strategy? Let's start a conversation
            about how we can help you achieve your goals.
          </p>
        </motion.div>
      </section>

      {/* Contact Form & Info */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="glass rounded-3xl p-8 md:p-10 relative overflow-hidden">
              {/* Glow effect */}
              <div className="absolute -top-32 -right-32 w-64 h-64 bg-[#00C896]/10 blur-[100px] rounded-full" />

              {isSubmitted ? (
                <div className="flex flex-col items-center justify-center py-16 relative z-10">
                  <CheckCircle className="w-20 h-20 text-[#00C896] mb-6" />
                  <h2
                    className="text-3xl font-semibold text-white mb-3 text-center"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    Message Received!
                  </h2>
                  <p className="text-slate-400 text-center max-w-md">
                    Thank you for reaching out. A member of our team will be in
                    touch within 24 hours to discuss your needs.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-testid="contact-form"
                  className="space-y-6 relative z-10"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Name */}
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm text-slate-400 mb-2"
                      >
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        data-testid="contact-input-name"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        required
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-600 focus:border-[#00C896]/50 focus:outline-none focus:ring-2 focus:ring-[#00C896]/20 transition-colors"
                        placeholder="John Smith"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm text-slate-400 mb-2"
                      >
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        data-testid="contact-input-email"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        required
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-600 focus:border-[#00C896]/50 focus:outline-none focus:ring-2 focus:ring-[#00C896]/20 transition-colors"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm text-slate-400 mb-2"
                    >
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      data-testid="contact-input-phone"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      required
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-600 focus:border-[#00C896]/50 focus:outline-none focus:ring-2 focus:ring-[#00C896]/20 transition-colors"
                      placeholder="10-digit mobile number"
                    />
                  </div>

                  {/* Company */}
                  <div>
                    <label
                      htmlFor="company"
                      className="block text-sm text-slate-400 mb-2"
                    >
                      Company Name (Optional)
                    </label>
                    <input
                      type="text"
                      id="company"
                      data-testid="contact-input-company"
                      value={formData.company}
                      onChange={(e) =>
                        setFormData({ ...formData, company: e.target.value })
                      }
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-600 focus:border-[#00C896]/50 focus:outline-none focus:ring-2 focus:ring-[#00C896]/20 transition-colors"
                      placeholder="Your Company Name"
                    />
                  </div>

                  {/* Service */}
                  <div>
                    <label
                      htmlFor="service"
                      className="block text-sm text-slate-400 mb-2"
                    >
                      Service Interested In *
                    </label>
                    <select
                      id="service"
                      data-testid="contact-select-service"
                      value={formData.service}
                      onChange={(e) =>
                        setFormData({ ...formData, service: e.target.value })
                      }
                      onBlur={() => {
                        if (formData.service) {
                          trackEvent("contact_service_selected", {
                            service_name: formData.service,
                          });
                        }
                      }}
                      required
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#00C896]/50 focus:outline-none focus:ring-2 focus:ring-[#00C896]/20 transition-colors appearance-none cursor-pointer"
                    >
                      <option value="" className="bg-[#0A0A0A]">
                        Select a service
                      </option>
                      {serviceCategories.map((category) => (
                        <optgroup key={category.label} label={category.label} className="bg-[#0A0A0A]">
                          {category.services.map((service) => (
                            <option
                              key={service.id}
                              value={service.label}
                              className="bg-[#0A0A0A]"
                            >
                              {service.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                      <option value="Other" className="bg-[#0A0A0A]">Other / Not Sure</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm text-slate-400 mb-2"
                    >
                      Tell Us About Your Needs
                    </label>
                    <textarea
                      id="message"
                      data-testid="contact-textarea-message"
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                      rows={5}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-600 focus:border-[#00C896]/50 focus:outline-none focus:ring-2 focus:ring-[#00C896]/20 transition-colors resize-none"
                      placeholder="Share some context about your company, stage, and what you're looking for..."
                    />
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={isSubmitting}
                    className="w-full bg-[#00C896] text-black font-bold px-8 py-4 rounded-full hover:bg-[#00E6AC] transition-colors shadow-[0_0_20px_rgba(0,200,150,0.3)] hover:shadow-[0_0_30px_rgba(0,200,150,0.5)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Send Message"
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-5"
          >
            <div className="space-y-6">
              <h2
                className="text-2xl font-semibold text-white mb-8"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                Other Ways to Reach Us
              </h2>

              {contactInfo.map((item) => (
                <div
                  key={item.label}
                  className="flex items-start gap-4 p-5 rounded-xl bg-[#0A0A0A] border border-white/5"
                >
                  <div className="w-12 h-12 rounded-lg bg-[#00C896]/10 flex items-center justify-center shrink-0">
                    <item.icon className="w-5 h-5 text-[#00C896]" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">{item.label}</p>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="text-white hover:text-[#00C896] transition-colors"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-white">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}

              {/* Calendar Booking */}
              <div className="mt-10 p-6 rounded-2xl bg-gradient-to-br from-[#00C896]/10 to-transparent border border-[#00C896]/20">
                <h3
                  className="text-xl font-semibold text-white mb-3"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  Prefer to Schedule Directly?
                </h3>
                <p className="text-slate-400 mb-4">
                  Book a 30-minute discovery call with our team to discuss your
                  financial goals.
                </p>
                <button
                  data-testid="schedule-call-btn"
                  className="border border-[#00C896]/50 text-[#00C896] px-6 py-3 rounded-full font-medium hover:bg-[#00C896]/10 transition-colors w-full"
                >
                  Schedule a Call
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Map Section (Placeholder) */}
      <section className="h-80 bg-[#0A0A0A] relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <MapPin className="w-12 h-12 text-[#00C896]/30 mx-auto mb-4" />
            <p className="text-slate-500">
              B4 ,313 First Floor, Keshav Puram Delhi 110035
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
